import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import { getImageUrl, loadScreenshotSliding } from "../lib/screenshotApi";
import { isScreenshotAccessExpired } from "../lib/screenshotAccess";
import { APP_ICON_2_PATH } from "../routes";
import { getScreenshotScene, isScreenshotSceneConfigured } from "../screenshotScenes";

/** App icon 2 canvas. */
export const APP_ICON_2_SIZE = 512;

const SAVE_BAR_H = 64;
const APP_ICON_2_FILE_NAME = "sliding-puzzle-quest-app-icon-2-512x512.png";

/** Same Funny kitchen / waffle puzzle as `/screenshot/05`. */
const APP_ICON_2_SCENE_SLUG = "05";

const PUBLIC = process.env.PUBLIC_URL || "";
/** Isolated waffle-face art for the center hero tile. */
const WAFFLE_FACE_ART = `${PUBLIC}/ad/sliding-app-icon-2-ref.png`;

const GRID = 3;
const EMPTY_CELL = 4; // center empty behind floating waffle (shows slide path glow)
const WAFFLE_HOME = 4;

function tileBackgroundPosition(sliceIndex, gridSize) {
    const gs = Math.max(1, Number(gridSize) || 1);
    const col = sliceIndex % gs;
    const row = Math.floor(sliceIndex / gs);
    if (gs <= 1) return "0% 0%";
    return `${(col / (gs - 1)) * 100}% ${(row / (gs - 1)) * 100}%`;
}

/**
 * App icon 2 — full-bleed dark blue background, neon 3×3 sliding grid lines,
 * bright waffle-face tile (no jigsaw outlines).
 */
export default function AppIcon2Page() {
    const scene = getScreenshotScene(APP_ICON_2_SCENE_SLUG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    const expired = isScreenshotAccessExpired();

    useEffect(() => {
        if (expired) {
            setLoading(false);
            return undefined;
        }
        if (!scene || !isScreenshotSceneConfigured(scene)) {
            setError("App icon scene is not configured");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [expired, scene]);

    const imageUrl = useMemo(() => getImageUrl(group?.story), [group]);

    useEffect(() => {
        if (expired || loading || error || !imageUrl) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({
            path: APP_ICON_2_PATH,
            slug: "app-icon-2",
        });
        return undefined;
    }, [expired, loading, error, imageUrl]);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / APP_ICON_2_SIZE, availableH / APP_ICON_2_SIZE),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );
            const dataUrl = await toPng(node, {
                width: APP_ICON_2_SIZE,
                height: APP_ICON_2_SIZE,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
            });
            const link = document.createElement("a");
            link.download = APP_ICON_2_FILE_NAME;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    const ready = Boolean(imageUrl);
    const cells = Array.from({ length: GRID * GRID }, (_, i) => i);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#020617",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(APP_ICON_2_SIZE * previewScale),
                    height: Math.round(APP_ICON_2_SIZE * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="sliding-app-icon-2"
                    sx={{
                        width: APP_ICON_2_SIZE,
                        height: APP_ICON_2_SIZE,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 0,
                        // Full-bleed dark technical background
                        background: `
                            radial-gradient(circle at 50% 48%, rgba(14, 165, 233, 0.2) 0%, transparent 52%),
                            linear-gradient(165deg, #0c4a6e 0%, #082f49 32%, #020617 70%, #000 100%)
                        `,
                    }}
                >
                    {loading ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={36} sx={{ color: "#38bdf8" }} />
                        </Box>
                    ) : error ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 3,
                            }}
                        >
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        </Box>
                    ) : ready ? (
                        <>
                            {/* Full-bleed 3×3 sliding grid */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${GRID}, 1fr)`,
                                    gridTemplateRows: `repeat(${GRID}, 1fr)`,
                                    gap: "6px",
                                    p: 0,
                                    boxSizing: "border-box",
                                    borderRadius: 0,
                                    bgcolor: "transparent",
                                    border: "none",
                                    boxShadow: "none",
                                    zIndex: 1,
                                }}
                            >
                                {cells.map((index) => {
                                    const isEmpty = index === EMPTY_CELL;
                                    const sliceIndex = index === WAFFLE_HOME ? 1 : index;
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                minWidth: 0,
                                                minHeight: 0,
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                                border: isEmpty
                                                    ? "2px solid rgba(56, 189, 248, 0.85)"
                                                    : "2px solid rgba(56, 189, 248, 0.55)",
                                                bgcolor: isEmpty
                                                    ? "rgba(2, 6, 23, 0.92)"
                                                    : "transparent",
                                                boxShadow: isEmpty
                                                    ? `
                                                        0 0 14px rgba(14, 165, 233, 0.55),
                                                        inset 0 0 18px rgba(14, 165, 233, 0.28)
                                                    `
                                                    : "inset 0 0 8px rgba(2, 6, 23, 0.4)",
                                                // Quiet surrounding tiles — still readable
                                                ...(isEmpty
                                                    ? {}
                                                    : {
                                                          opacity: 0.72,
                                                          filter: "brightness(0.55) saturate(0.55)",
                                                          backgroundImage: `url("${imageUrl}")`,
                                                          backgroundSize: `${GRID * 100}% ${GRID * 100}%`,
                                                          backgroundPosition: tileBackgroundPosition(
                                                              sliceIndex,
                                                              GRID,
                                                          ),
                                                          backgroundRepeat: "no-repeat",
                                                      }),
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            {/* Bright waffle-face hero — face only */}
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    width: 248,
                                    height: 248,
                                    marginLeft: "-124px",
                                    marginTop: "-124px",
                                    borderRadius: "16px",
                                    overflow: "hidden",
                                    transform: "rotate(-8deg) scale(1.16)",
                                    zIndex: 4,
                                    // Zoom into the smiling waffle face from the face art
                                    backgroundImage: `url("${WAFFLE_FACE_ART}")`,
                                    backgroundSize: "145% 145%",
                                    backgroundPosition: "50% 48%",
                                    backgroundRepeat: "no-repeat",
                                    filter: "brightness(1.1) saturate(1.2) contrast(1.05)",
                                    border: "4px solid rgba(125, 211, 252, 0.98)",
                                    boxShadow: `
                                        0 0 0 2px rgba(14, 165, 233, 0.8),
                                        0 0 28px rgba(56, 189, 248, 0.9),
                                        0 0 40px rgba(251, 191, 36, 0.4),
                                        0 12px 24px rgba(0, 0, 0, 0.5)
                                    `,
                                }}
                            />

                            {/* Motion ticks around the face tile */}
                            {[
                                { top: "28%", left: "42%", w: 18, rot: -20 },
                                { top: "32%", left: "58%", w: 14, rot: 25 },
                                { top: "62%", left: "38%", w: 16, rot: 15 },
                                { top: "66%", left: "60%", w: 12, rot: -30 },
                            ].map((m, i) => (
                                <Box
                                    key={i}
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        top: m.top,
                                        left: m.left,
                                        width: m.w,
                                        height: 2,
                                        borderRadius: 1,
                                        bgcolor: "rgba(186, 230, 253, 0.75)",
                                        boxShadow: "0 0 6px rgba(56, 189, 248, 0.8)",
                                        transform: `rotate(${m.rot}deg)`,
                                        zIndex: 5,
                                        pointerEvents: "none",
                                    }}
                                />
                            ))}
                        </>
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="text.secondary">Puzzle image missing.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "rgba(2, 6, 23, 0.92)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <SaveAltIcon />
                        )
                    }
                    onClick={handleSave}
                    disabled={!ready || saving || loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {saving ? "Saving…" : "Save 512×512 PNG"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
